import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Layers, Tag, Sliders, ArrowLeft, ShieldCheck, RefreshCw, TrendingUp, Book,
} from 'lucide-react'
import { PageHeader, Button } from '@/components/ui'
import { ArticleGroupsSection } from '../sections/ArticleGroupsSection.tsx'
import { ArticlePriceGroupsSection } from '../sections/ArticlePriceGroupsSection.tsx'
import { PriceAdjustmentSection } from '../sections/PriceAdjustmentSection.tsx'
import { DefaultsSection } from '../sections/DefaultsSection.tsx'
import { WarrantiesSection } from '../sections/WarrantiesSection.tsx'
import { SapSyncSection } from '../sections/SapSyncSection.tsx'
import { ServicekatalogCategoriesSection } from '../sections/ServicekatalogCategoriesSection.tsx'
import { ServicekatalogSettingsSection } from '../sections/ServicekatalogSettingsSection.tsx'
import { cn } from '@/lib/utils.ts'

type ArtikelSettingsTab = 'sap-sync' | 'groups' | 'price-groups' | 'price-adjustment' | 'defaults' | 'warranties' | 'servicekatalog-categories' | 'servicekatalog'

const TABS: { key: ArtikelSettingsTab; label: string; icon: typeof Layers }[] = [
  { key: 'sap-sync', label: 'SAP Sync', icon: RefreshCw },
  { key: 'groups', label: 'Warengruppen', icon: Layers },
  { key: 'price-groups', label: 'Preisgruppen', icon: Tag },
  { key: 'price-adjustment', label: 'Preisanpassung', icon: TrendingUp },
  { key: 'defaults', label: 'Defaults', icon: Sliders },
  { key: 'warranties', label: 'Garantien', icon: ShieldCheck },
  { key: 'servicekatalog', label: 'Servicekatalog', icon: Book },
  { key: 'servicekatalog-categories', label: 'SK-Kategorien', icon: Book },
]

export function ArtikelSettingsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as ArtikelSettingsTab | null
  const [activeTab, setActiveTab] = useState<ArtikelSettingsTab>(
    TABS.some((t) => t.key === tabParam) ? tabParam! : 'sap-sync',
  )

  function handleTabChange(tab: ArtikelSettingsTab) {
    setActiveTab(tab)
    setSearchParams({ tab }, { replace: true })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Artikelverwaltung – Einstellungen"
        description="Warengruppen, Preisgruppen, Standard-Werte und Garantien"
        actions={
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate('/artikel')}
          >
            Zurück zu Artikel
          </Button>
        }
        breadcrumbs={[
          { label: 'Einstellungen', to: '/einstellungen' },
          { label: 'Artikelverwaltung' },
        ]}
      />

      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => handleTabChange(key)}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === key
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700',
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'sap-sync' && <SapSyncSection />}
      {activeTab === 'groups' && <ArticleGroupsSection />}
      {activeTab === 'price-groups' && <ArticlePriceGroupsSection />}
      {activeTab === 'price-adjustment' && <PriceAdjustmentSection />}
      {activeTab === 'defaults' && <DefaultsSection />}
      {activeTab === 'warranties' && <WarrantiesSection />}
      {activeTab === 'servicekatalog' && <ServicekatalogSettingsSection />}
      {activeTab === 'servicekatalog-categories' && <ServicekatalogCategoriesSection />}
    </div>
  )
}

